import React, { useEffect } from "react";
import styles from "./breadcrumps.module.css";
import { Box, Skeleton } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { ProfileMenu } from "../navbar/profileMenu/profileMenu.component";
import { NotificationsBell } from "./notificationsBell.component";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "./breadcrumbsContext";

// UUID validation function
const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const Breadcrumbs = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { overrides, linkOverrides } = useBreadcrumbs();
  const pathnames = location.pathname.split("/").filter((x) => x);

  // Filter out "home" from pathnames if it exists, since we always show it first
  const filteredPathnames = pathnames.filter((name) => name !== "home");

  useEffect(() => {
    const labels = [
      t("Home"),
      ...filteredPathnames.map((value, index) => {
        const to = "/" + filteredPathnames.slice(0, index + 1).join("/");
        const decodedValue = decodeURIComponent(value);
        const capitalizedValue = decodedValue.charAt(0).toUpperCase() + decodedValue.slice(1);
        return overrides[to] || t(capitalizedValue) || decodedValue;
      }),
    ].filter(Boolean);

    const lastLabel = labels[labels.length - 1];
    document.title = lastLabel ? `${lastLabel} | Copigo` : "Copigo";
  }, [filteredPathnames, overrides, t]);

  const content = (
    <nav>
      <ol style={{ display: "flex", gap: "0.3rem", listStyle: "none", padding: 20, textDecoration: "none" }}>
        <li style={{ display: "flex", gap: "0.3rem", alignItems: "center" }} key="/home">
          <Link className={styles.link} style={{ textDecoration: 'none' }} to="/home">
            {t("Home")}
          </Link>
        </li>

        {filteredPathnames.map((value, index) => {
          const to = "/" + filteredPathnames.slice(0, index + 1).join("/");
          const decodedValue = decodeURIComponent(value);
          const capitalizedValue = decodedValue.charAt(0).toUpperCase() + decodedValue.slice(1);

          // Check if there's a custom override for this path
          const label = overrides[to] || t(capitalizedValue);
          const isLoading = isUUID(decodedValue) && !overrides[to];
          const linkTo = linkOverrides[to] || to;

          return (
            <li style={{ display: "flex", gap: "0.3rem", alignItems: "center" }} key={to}>
              <span>&gt;</span>
              {isLoading ? (
                <Skeleton variant="text" width={100} height={24} />
              ) : (
                <Link className={styles.link} style={{ textDecoration: 'none' }} to={linkTo}>{label}</Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );

  return (
    <Box className={styles.breadcrumbsBar}>
      <Box className={styles.breadcrumb}>
        {content}
      </Box>
      <Box className={styles.actionsBox}>
        <NotificationsBell />
        <Box className={styles.profileBox}>
          <ProfileMenu />
        </Box>
      </Box>
    </Box>
  );
};
